(() => {
  'use strict';
  if (window.WellnessMergeInteractions) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const accordions = new WeakMap();
  const menus = new WeakMap();
  const topicObservers = new WeakMap();

  function each(root, selector, callback) {
    if (root.matches?.(selector)) callback(root);
    root.querySelectorAll(selector).forEach(callback);
  }

  function finishAccordion(details, state) {
    state.animation?.cancel();
    state.animation = null;
    details.open = state.expanded;
    details.removeAttribute('data-cc-closing');
    state.body.style.height = '';
    state.body.style.opacity = '';
  }

  function setAccordion(details, expanded, animate = true) {
    const state = accordions.get(details);
    if (!state) return;
    if (expanded && !details.closest('[data-cc-faq-multiple]')) {
      details.closest('.cc-faq-list')?.querySelectorAll('.cc-faq-item').forEach(sibling => {
        const other = accordions.get(sibling);
        if (sibling !== details && (other?.expanded || sibling.open)) setAccordion(sibling, false, animate);
      });
    }
    if (expanded === state.expanded && !state.animation && details.open === expanded) return;
    state.expanded = expanded;
    state.summary.setAttribute('aria-expanded', String(expanded));
    finishAccordion(details, state);
  }

  function connectAccordion(details) {
    if (accordions.has(details)) return;
    const summary = details.querySelector(':scope > summary');
    const body = details.querySelector(':scope > .cc-faq-answer');
    if (!summary || !body) return;
    const state = { summary, body, expanded: details.open, animation: null };
    accordions.set(details, state);
    details.removeAttribute('name');
    summary.setAttribute('aria-expanded', String(state.expanded));
    summary.addEventListener('click', event => {
      event.preventDefault();
      setAccordion(details, !state.expanded);
    });
    details.addEventListener('toggle', () => {
      if (!state.animation && details.open !== state.expanded) setAccordion(details, details.open, false);
    });
  }

  function closeMenu(details, animate = true) {
    const state = menus.get(details);
    if (!state) { details.open = false; return; }
    clearTimeout(state.timer);
    state.animation?.cancel();
    state.summary.setAttribute('aria-expanded', 'false');
    if (!details.open || !animate || reduced.matches) { details.open = false; return; }
    const animation = state.list.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(7px)' }], { duration: 140, easing: 'ease-in', fill: 'both' });
    state.animation = animation;
    animation.onfinish = () => {
      if (state.animation !== animation) return;
      details.open = false;
      animation.cancel();
      state.animation = null;
    };
  }

  function openMenu(details) {
    const state = menus.get(details);
    if (!state) return;
    clearTimeout(state.timer);
    state.animation?.cancel();
    state.animation = null;
    document.querySelectorAll('.cc-nav-sub[open]').forEach(other => { if (other !== details) closeMenu(other, false); });
    details.open = true;
    state.summary.setAttribute('aria-expanded', 'true');
  }

  function connectMenu(details) {
    if (menus.has(details)) return;
    const summary = details.querySelector(':scope > summary');
    const list = details.querySelector(':scope > ul');
    if (!summary || !list) return;
    const state = { summary, list, timer: null, animation: null };
    menus.set(details, state);
    summary.setAttribute('aria-expanded', String(details.open));
    summary.addEventListener('click', event => { event.preventDefault(); details.open ? closeMenu(details) : openMenu(details); });
    details.addEventListener('pointerenter', () => { if (hover.matches) openMenu(details); });
    details.addEventListener('pointerleave', () => {
      if (hover.matches && !details.contains(document.activeElement)) state.timer = setTimeout(() => closeMenu(details), 100);
    });
    details.addEventListener('focusout', event => { if (!details.contains(event.relatedTarget)) closeMenu(details); });
  }

  function connectTopics(nav) {
    if (topicObservers.has(nav)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const pairs = [...nav.querySelectorAll('a[href^="#cc-faq-"]')].map(link => ({link, group: document.getElementById(link.hash.slice(1))})).filter(pair => pair.group);
    if (!pairs.length) return;
    let queued = false;
    let lockedUntil = 0;
    const activate = id => pairs.forEach(({link, group}) => group.id === id ? link.setAttribute('aria-current', 'location') : link.removeAttribute('aria-current'));
    const update = () => {
      queued = false;
      if (!nav.isConnected || performance.now() < lockedUntil) return;
      const header = document.querySelector('[data-cc-header]');
      const offset = Math.max(110, (header?.getBoundingClientRect().bottom || 0) + 28);
      let current = pairs[0];
      pairs.forEach(pair => { if (pair.group.getBoundingClientRect().top <= offset) current = pair; });
      activate(current.group.id);
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    pairs.forEach(({link,group}) => link.addEventListener('click', () => {
      lockedUntil = performance.now() + (reduced.matches ? 0 : 1000);
      activate(group.id);
      setTimeout(queue, reduced.matches ? 0 : 1050);
    }, {signal}));
    window.addEventListener('scroll', queue, {passive:true,signal});
    window.addEventListener('resize', queue, {passive:true,signal});
    topicObservers.set(nav, {disconnect:()=>controller.abort()});
    queue();
  }
  function openHashAnswer() {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    if (!id) return;
    const target = document.getElementById(id);
    if (!target?.matches('.cc-faq-item')) return;
    setAccordion(target, true, false);
  }

  function init(root = document) {
    each(root, '.cc-faq-item', connectAccordion);
    each(root, '.cc-nav-sub', connectMenu);
    each(root, '[data-cc-faq-nav]', connectTopics);
    openHashAnswer();
  }

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = event.target instanceof Element ? event.target.closest('.cc-nav-sub[open]') : null;
    if (!menu) return;
    closeMenu(menu, false);
    menus.get(menu)?.summary.focus();
    event.preventDefault();
  }, true);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    each(event.target, '[data-cc-faq-nav]', nav => topicObservers.get(nav)?.disconnect());
    each(event.target, '.cc-faq-item', details => {
      const state = accordions.get(details);
      if (state) finishAccordion(details, state);
    });
  });
  function settle() {
    document.querySelectorAll('.cc-faq-item').forEach(details => {
      const state = accordions.get(details);
      if (state?.animation) finishAccordion(details, state);
    });
  }
  window.addEventListener('resize', settle, { passive: true });
  reduced.addEventListener('change', settle);
  window.addEventListener('hashchange', openHashAnswer);
  window.WellnessMergeInteractions = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
